import { IAggFunc, IAggFuncParams, RowNode } from 'ag-grid-enterprise';

import { SDuration } from '@summit/core';
import { Duration } from 'dayjs/plugin/duration';
import { DisplayType } from './display-type.enum';
import { EquationTypes } from './equation-types.enum';
import { IDisplaySchema } from './i-display-schema';
import { SummitDisplay } from './summit-display';
import { SummitFormattedText } from './summit-formatted-text';
import { SummitIdObject } from './summit-id-object';
import { ViewField } from './view/view-field';

export class SummitAggregateFunctions {
  public static GetAggregateFunction<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): string | IAggFunc | undefined {
    switch (attribute.footerEquation) {
      case EquationTypes.Sum:
        return SummitAggregateFunctions.GetSumFunction(schema, attribute);
      case EquationTypes.SumBy:
      case EquationTypes.SumByAndDisplayBy:
        return SummitAggregateFunctions.GetSumByFunction(schema, attribute);
      case EquationTypes.PercentageOf:
        return SummitAggregateFunctions.GetDividedByFunction(schema, attribute);
      case EquationTypes.DividedBy:
        return SummitAggregateFunctions.GetDividedByFunction(schema, attribute);
      case EquationTypes.First:
        return SummitAggregateFunctions.GetFirstFunction(schema, attribute);
      case EquationTypes.Custom:
        throw new Error('Not implemented - Add to ViewField');
      default:
        return undefined;
    }
  }

  public static ComputeAggregate<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>,
    data: T[]
  ): SummitFormattedText | number | undefined | Duration {
    //console.log(`Computing Aggregate for ${attribute.propertyName.toString()} with ${data.length} records`, data);
    switch (attribute?.footerEquation) {
      case EquationTypes.Sum:
        attribute.footerEquationValues ??= [attribute.propertyName.toString()];
        if (attribute.footerEquationValues && attribute.footerEquationValues.length > 0) {
          const displayType = attribute.displayType;
          const footerEquationValue = attribute.footerEquationValues[0];
          if (displayType == DisplayType.Duration) {
            return data
              .map((entry) => (entry[footerEquationValue] ?? SDuration.new()) as unknown as Duration)
              .reduce((a: Duration, b: Duration) => a.add(b ?? SDuration.new()), SDuration.new());
          }
          return data.map((entry) => entry[footerEquationValue] as unknown as number).reduce((a, b) => a + (b ?? 0), 0);
        }
        return data
          .map((entry) => entry[attribute.propertyName] as unknown as number)
          .reduce((a, b) => a + (b ?? 0), 0);
      case EquationTypes.SumBy:
      case EquationTypes.SumByAndDisplayBy: {
        const sumByColumns = SummitAggregateFunctions.GetSumByColumns(schema, attribute);
        return this.ComputeSumBy(schema, data, sumByColumns);
      }
      case EquationTypes.First:
        if (data.length == 0) return undefined;
        const reducedArray = data
          .map((entry) => entry[attribute.propertyName])
          .filter((value) => value !== undefined && value !== null);
        if (reducedArray.length == 0) return undefined;
        return reducedArray[0] as unknown as SummitFormattedText;
      case EquationTypes.PercentageOf:
      case EquationTypes.DividedBy:
        if (!attribute.footerEquationValues || attribute.footerEquationValues.length != 2) return undefined;
        return this.ComputeDivideBy(schema, data, attribute.footerEquationValues[0], attribute.footerEquationValues[1]);
      case EquationTypes.Custom:
        throw new Error('Not implemented - Add to ViewField');
      default:
        return undefined;
    }
  }

  private static GetSumFunction<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): IAggFunc | string | undefined {
    if (!attribute.footerEquationValues || attribute.footerEquationValues.length != 1) return 'sum';
    const displayType = attribute.displayType;
    //console.log(`Getting Sum Function for ${attribute.propertyName.toString()} with ${displayType}`);
    if (displayType == DisplayType.Duration) {
      return (params: IAggFuncParams): Duration | undefined => {
        console.log(`Computing Duration for ${attribute.propertyName.toString()} with ${params.values.length} records`);
        if (params.values.length == 0) return undefined;
        const dataNodes = this.getAllFilteredLeafNodes(params.rowNode as RowNode<T>);
        const dataRows = dataNodes
          .map((row) => row.data)
          .filter((entry) => entry && (!attribute.footerFilter || attribute.footerFilter(entry)));
        const property = attribute.footerEquationValues?.[0];
        return dataRows
          .map((entry) => entry[property] as unknown as Duration)
          .map((entry) => entry ?? SDuration.new())
          .reduce((a: Duration, b: Duration) => a.add(b ?? SDuration.new()), SDuration.new());
      };
    }
    return (params: IAggFuncParams): number | undefined => {
      if (params.values.length == 0) return undefined;
      const dataNodes = this.getAllFilteredLeafNodes(params.rowNode as RowNode<T>);
      const dataRows = dataNodes
        .map((row) => row.data)
        .filter((entry) => entry && (!attribute.footerFilter || attribute.footerFilter(entry)));
      const property = attribute.footerEquationValues?.[0];
      return dataRows.map((entry) => entry[property] as unknown as number).reduce((a, b) => a + (b ?? 0), 0);
    };
  }

  private static GetFirstFunction<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): IAggFunc | string | undefined {
    return (params: IAggFuncParams): SummitFormattedText | undefined => {
      if (params.values.length == 0) return undefined;
      var lclValues = params.values.filter((value) => value !== undefined && value !== null);
      if (lclValues.length == 0) return undefined;
      return lclValues[0] as unknown as SummitFormattedText;
    };
  }

  private static GetSumByFunction<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): IAggFunc | undefined {
    if (!attribute.footerEquationValues) return undefined;
    const sumByColumns = SummitAggregateFunctions.GetSumByColumns(schema, attribute);
    const valueAttribute =
      sumByColumns.valueField == attribute.propertyName ? attribute : schema.getAttribute(sumByColumns.valueField);
    const displayGroupByAttribute = schema.getAttribute(sumByColumns.displayGroupField);
    return (params: IAggFuncParams): SummitFormattedText | undefined => {
      if (params.values.length == 0) return undefined;
      const dataNodes = this.getAllFilteredLeafNodes(params.rowNode as RowNode<T>);
      const dataRows = dataNodes
        .map((row) => row.data)
        .filter((entry) => entry && (!attribute.footerFilter || attribute.footerFilter(entry)));
      return this.ComputeSumBy(schema, dataRows, sumByColumns, valueAttribute, displayGroupByAttribute);
    };
  }

  private static getAllFilteredLeafNodes(rowNode: RowNode): RowNode[] {
    const total = [] as RowNode[];
    if (rowNode.childrenAfterSort) {
      rowNode.childrenAfterSort.forEach((child) => {
        if (child.group) {
          total.push(...this.getAllFilteredLeafNodes(child));
        } else {
          total.push(child);
        }
      });
    }
    return total;
  }

  private static GetSumByColumns<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): { byField: keyof T | undefined; valueField: keyof T | undefined; displayGroupField: keyof T | undefined } {
    let byIndex = 0;
    let groupIndex = 0;
    let valueField: keyof T | undefined;
    //View Field selection
    if (attribute.footerEquation == EquationTypes.SumBy && attribute.footerEquationValues) {
      if (attribute.footerEquationValues.length == 1) {
        valueField = attribute.propertyName;
      } else {
        valueField = attribute.footerEquationValues[0];
        byIndex = 1;
        groupIndex = 1;
      }
    } else {
      if (attribute.footerEquationValues?.length == 2) {
        valueField = attribute.propertyName;
        byIndex = 0;
        groupIndex = 1;
      } else {
        valueField = attribute.footerEquationValues?.[0];
        byIndex = 1;
        groupIndex = 2;
      }
    }
    let valueAttribute: ViewField<T> | undefined;
    if (valueField === attribute.propertyName) {
      valueAttribute = attribute;
    } else {
      valueAttribute = schema.getAttribute(valueField);
    }
    if (!valueAttribute) {
      console.error(`Could not find attributes for:`, attribute.footerEquationValues);
    }
    return {
      byField: attribute.footerEquationValues?.[byIndex],
      valueField: valueField,
      displayGroupField: attribute.footerEquationValues?.[groupIndex]
    };
  }

  public static ComputeSumBy<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    dataRows: T[],
    sumByColumns: {
      byField: keyof T | undefined;
      valueField: keyof T | undefined;
      displayGroupField: keyof T | undefined;
    },
    valueAttribute?: ViewField<T>,
    displayGroupByAttribute?: ViewField<T>
  ): SummitFormattedText {
    const totalDictionary = new Map<string, number>();
    const objectDictionary = new Map();
    const response = new SummitFormattedText();
    response.ellipsisAfterLine = 5;
    const valueField = sumByColumns.valueField;
    valueAttribute ??= schema.getAttribute(valueField);
    displayGroupByAttribute ??= schema.getAttribute(sumByColumns.displayGroupField);
    // if (valueAttribute.propertyName == 'driverAsk')
    //   console.log(`Compute Sum By ${valueAttribute.propertyName} with ${dataRows.length} records`, dataRows);
    if (dataRows) {
      dataRows
        .filter((entry) => entry)
        .forEach((entry) => {
          const label = sumByColumns.byField ? (entry[sumByColumns.byField] as unknown as string) : undefined;
          //console.log(`Checking to see if ${byField}: ${label} is in`, totalDictionary);
          if (label && totalDictionary.has(label)) {
            //console.log(`Adding ${entry[valueField] ?? 0} to ${totalDictionary[label]} for ${label}`);
            const item1 = totalDictionary.get(label) ?? 0;
            const item2 = valueField ? ((entry[valueField] ?? 0) as unknown as number) : 0;
            totalDictionary.set(label, item1 + item2);
          } else {
            if (valueField && (label || entry[valueField])) {
              //console.log(`Creating ${entry[valueField]} for ${label}`);
              totalDictionary.set(label ?? 'Unknown', (entry[valueField] ?? 0) as unknown as number);
              objectDictionary.set(label, entry);
            }
          }
        });
    }
    Array.from(totalDictionary.keys()).forEach((key) => {
      if (key !== undefined && key !== null) {
        const newKeyValue = displayGroupByAttribute
          ? schema.getTextLabel(objectDictionary.get(key), displayGroupByAttribute)
          : undefined;
        // if (valueAttribute.propertyName == 'driverAsk')
        //   console.log(`Looked up ${key} got ${newKeyValue} using `, displayGroupByAttribute);
        if (valueAttribute) {
          const newValue = SummitDisplay.formatAutoTextLabel(totalDictionary.get(key), valueAttribute);
          if (newValue) response.AddKeyValues(newKeyValue?.toString() ?? 'Unknown', newValue);
        }
      }
    });
    response.RemoveZeroValues();
    response.SortAlphabetically();
    //console.log(`Computed Sum By ${valueAttribute?.propertyName} with ${dataRows.length} records`, response);
    // if (valueAttribute.propertyName == 'driverAsk' && response.lines.length > 0) console.log(`Response`, response);
    return response;
  }
  public static GetDividedByFunction<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): IAggFunc | undefined {
    if (!attribute.footerEquationValues) return undefined;
    const numeratorKey = attribute.footerEquationValues[0];
    const denominatorKey = attribute.footerEquationValues[1];
    return (params: IAggFuncParams): number | undefined => {
      if (params.values.length == 0) return undefined;
      const dataNodes = this.getAllFilteredLeafNodes(params.rowNode as RowNode<T>);
      const dataRows = dataNodes.map((row) => row.data);
      return this.ComputeDivideBy(schema, dataRows, numeratorKey, denominatorKey);
    };
  }

  public static ComputeDivideBy<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    dataRows: T[],
    numeratorKey: keyof T,
    denominatorKey: keyof T
  ): number {
    const numeratorTotal = dataRows
      .filter((x) => x)
      .reduce((a, b) => a + ((b[numeratorKey] as unknown as number) ?? 0), 0);
    const denominatorTotal = dataRows
      .filter((x) => x)
      .reduce((a, b) => a + ((b[denominatorKey] as unknown as number) ?? 0), 0);
    return numeratorTotal / denominatorTotal;
  }
}
